import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import styles from '../assets/guardianManagerDashboard.module.css';
import guardiansService from '../services/guardiansService';
import guardianQueryService from '../services/guardianQueryService';

// Sort Icon Component matching Laravel style
const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const GuardianManagerDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    totalGuardians: 0,
    placedGuardians: 0,
    documentsRequired: 0,
    pendingQueries: 0,
    guardiansAfterDeadline: 0,
    guardiansInProcess: 0
  });
  const [guardiansAfterDeadline, setGuardiansAfterDeadline] = useState([]);
  const [guardiansInProcess, setGuardiansInProcess] = useState([]);
  const [pendingQueries, setPendingQueries] = useState([]);

  // Table controls for Guardians After Deadline
  const [deadlineSortConfig, setDeadlineSortConfig] = useState({ key: null, direction: 'asc' });
  const [deadlineSearch, setDeadlineSearch] = useState('');
  const [deadlinePerPage, setDeadlinePerPage] = useState(10);
  const [deadlineCurrentPage, setDeadlineCurrentPage] = useState(1);

  // Table controls for Guardians In Process
  const [processSortConfig, setProcessSortConfig] = useState({ key: null, direction: 'asc' });
  const [processSearch, setProcessSearch] = useState('');
  const [processPerPage, setProcessPerPage] = useState(10);
  const [processCurrentPage, setProcessCurrentPage] = useState(1);

  // Table controls for Pending Queries
  const [queriesSortConfig, setQueriesSortConfig] = useState({ key: null, direction: 'asc' });
  const [queriesSearch, setQueriesSearch] = useState('');
  const [queriesPerPage, setQueriesPerPage] = useState(10);
  const [queriesCurrentPage, setQueriesCurrentPage] = useState(1);

  // Column visibility controls
  const [deadlineColumnVisibility, setDeadlineColumnVisibility] = useState({
    name: true,
    email: true,
    property: true,
    room: true,
    deadline: true,
    actions: true
  });
  const [showDeadlineColumnVisibility, setShowDeadlineColumnVisibility] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Check if user is loaded
      if (!user || !user.id) {
        console.warn('⚠️ [FRONTEND] User not loaded yet, waiting...');
        setLoading(false);
        return;
      }

      // Initialize variables for statistics calculation
      let deadlineData = [];
      let processData = [];
      let pendingQueriesData = [];

      // Load guardians after deadline
      try {
        deadlineData = await guardiansService.getGuardiansAfterDeadline();
        console.log('✅ [FRONTEND] Guardians after deadline loaded:', deadlineData);
        setGuardiansAfterDeadline(deadlineData || []);
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load guardians after deadline:', error);
        toast.error('Failed to load guardians after deadline');
        setGuardiansAfterDeadline([]);
      }

      // Load guardians in process
      try {
        const processResponse = await guardiansService.getGuardiansInProcess();
        console.log('✅ [FRONTEND] Guardians in process loaded:', processResponse.data);
        processData = processResponse.data || [];
        setGuardiansInProcess(processData);
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load guardians in process:', error);
        toast.error('Failed to load guardians in process');
        setGuardiansInProcess([]);
      }

      // Load pending queries
      try {
        const queriesResponse = await guardianQueryService.getGuardianQueries();
        console.log('✅ [FRONTEND] Guardian queries loaded:', queriesResponse);
        pendingQueriesData = queriesResponse.filter(q => q.status === 'pending' || q.status === 'open');
        setPendingQueries(pendingQueriesData);
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load guardian queries:', error);
        toast.error('Failed to load guardian queries');
        setPendingQueries([]);
      }

      // Load guardian statistics
      try {
        const overviewData = await guardiansService.getGuardianStatusOverview();
        console.log('✅ [FRONTEND] Guardian overview loaded:', overviewData);
        setStatistics({
          totalGuardians: overviewData.total || 0,
          placedGuardians: overviewData.placed || 0,
          documentsRequired: overviewData.documentsRequired || 0,
          pendingQueries: pendingQueriesData.length,
          guardiansAfterDeadline: deadlineData.length,
          guardiansInProcess: processData.length
        });
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load guardian statistics:', error);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      loadDashboardData();
    }
  }, [user, loadDashboardData]);

  // Sorting functions matching Laravel pattern
  const handleSort = (key, sortConfig, setSortConfig) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data, sortConfig) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle nested properties
      if (sortConfig.key === 'name') {
        aValue = `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim();
        bValue = `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.trim();
      }
      if (sortConfig.key === 'email') {
        aValue = a.user?.email || '';
        bValue = b.user?.email || '';
      }
      if (sortConfig.key === 'property') {
        aValue = a.rents?.[0]?.room?.property?.name || '';
        bValue = b.rents?.[0]?.room?.property?.name || '';
      }
      if (sortConfig.key === 'room') {
        aValue = a.rents?.[0]?.room?.name || '';
        bValue = b.rents?.[0]?.room?.name || '';
      }
      if (sortConfig.key === 'deadline') {
        aValue = a.rents?.[0]?.date_to_send_documentation ? new Date(a.rents[0].date_to_send_documentation) : null;
        bValue = b.rents?.[0]?.date_to_send_documentation ? new Date(b.rents[0].date_to_send_documentation) : null;
      }
      if (sortConfig.key === 'property_name') {
        aValue = a.property?.name || '';
        bValue = b.property?.name || '';
      }
      if (sortConfig.key === 'guardian_name') {
        aValue = `${a.guardian?.user?.first_name || ''} ${a.guardian?.user?.last_name || ''}`.trim();
        bValue = `${b.guardian?.user?.first_name || ''} ${b.guardian?.user?.last_name || ''}`.trim();
      }
      if (sortConfig.key === 'subject') {
        aValue = a.subject || '';
        bValue = b.subject || '';
      }
      if (sortConfig.key === 'created_at') {
        aValue = a.created_at ? new Date(a.created_at) : null;
        bValue = b.created_at ? new Date(b.created_at) : null;
      }

      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Search filter functions
  const filterDeadlineGuardians = (guardians, searchTerm) => {
    if (!searchTerm) return guardians;
    return guardians.filter(guardian =>
      `${guardian.user?.first_name || ''} ${guardian.user?.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (guardian.user?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (guardian.rents?.[0]?.room?.property?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterProcessGuardians = (guardians, searchTerm) => {
    if (!searchTerm) return guardians;
    return guardians.filter(guardian =>
      `${guardian.guardian?.user?.first_name || ''} ${guardian.guardian?.user?.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (guardian.property?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filterQueries = (queries, searchTerm) => {
    if (!searchTerm) return queries;
    return queries.filter(query =>
      (query.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${query.guardian?.user?.first_name || ''} ${query.guardian?.user?.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Processed data with sorting, filtering and pagination
  const processedDeadlineGuardians = useMemo(() => {
    let filtered = filterDeadlineGuardians(guardiansAfterDeadline, deadlineSearch);
    let sorted = sortData(filtered, deadlineSortConfig);

    const totalPages = Math.ceil(sorted.length / deadlinePerPage);
    const startIndex = (deadlineCurrentPage - 1) * deadlinePerPage;
    const paginated = sorted.slice(startIndex, startIndex + deadlinePerPage);

    return { data: paginated, total: sorted.length, totalPages };
  }, [guardiansAfterDeadline, deadlineSearch, deadlineSortConfig, deadlinePerPage, deadlineCurrentPage]);

  const processedProcessGuardians = useMemo(() => {
    let filtered = filterProcessGuardians(guardiansInProcess, processSearch);
    let sorted = sortData(filtered, processSortConfig);

    const totalPages = Math.ceil(sorted.length / processPerPage);
    const startIndex = (processCurrentPage - 1) * processPerPage;
    const paginated = sorted.slice(startIndex, startIndex + processPerPage);

    return { data: paginated, total: sorted.length, totalPages };
  }, [guardiansInProcess, processSearch, processSortConfig, processPerPage, processCurrentPage]);

  const processedQueries = useMemo(() => {
    let filtered = filterQueries(pendingQueries, queriesSearch);
    let sorted = sortData(filtered, queriesSortConfig);

    const totalPages = Math.ceil(sorted.length / queriesPerPage);
    const startIndex = (queriesCurrentPage - 1) * queriesPerPage;
    const paginated = sorted.slice(startIndex, startIndex + queriesPerPage);

    return { data: paginated, total: sorted.length, totalPages };
  }, [pendingQueries, queriesSearch, queriesSortConfig, queriesPerPage, queriesCurrentPage]);

  // Column visibility handler
  const toggleDeadlineColumnVisibility = (columnKey) => {
    setDeadlineColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Export functions
  const handleExportDeadlineCSV = async () => {
    try {
      const data = processedDeadlineGuardians.data.map(guardian => ({
        'Name': `${guardian.user?.first_name || ''} ${guardian.user?.last_name || ''}`,
        'Email': guardian.user?.email || '',
        'Property': guardian.rents?.[0]?.room?.property?.name || '',
        'Room': guardian.rents?.[0]?.room?.name || '',
        'Deadline': guardian.rents?.[0]?.date_to_send_documentation ? new Date(guardian.rents[0].date_to_send_documentation).toLocaleDateString() : ''
      }));

      const csvContent = 'data:text/csv;charset=utf-8,' +
        [Object.keys(data[0] || {}).join(',')]
          .concat(data.map(row => Object.values(row).join(',')))
          .join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `guardians-after-deadline-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Data exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportDeadlinePDF = async () => {
    try {
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Guardians After Deadline Report', 14, 15);

      // Add user info
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Guardian Manager: ${user?.first_name} ${user?.last_name}`, 14, 22);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}`, 14, 27);

      // Prepare table data
      const tableData = processedDeadlineGuardians.data.map(guardian => [
        `${guardian.user?.first_name || ''} ${guardian.user?.last_name || ''}`,
        guardian.user?.email || '',
        guardian.rents?.[0]?.room?.property?.name || '',
        guardian.rents?.[0]?.room?.name || '',
        guardian.rents?.[0]?.date_to_send_documentation ? new Date(guardian.rents[0].date_to_send_documentation).toLocaleDateString('en-GB') : ''
      ]);

      // Add table
      doc.autoTable({
        head: [['Name', 'Email', 'Property', 'Room', 'Deadline']],
        body: tableData,
        startY: 32,
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [18, 39, 78],
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 32, left: 14, right: 14 }
      });

      // Save PDF
      const filename = `guardians-after-deadline-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbContainer}>
        <div className={styles.container}>
          <div className={styles.breadcrumb}>
            <span className={styles.dashboardTag}>Dashboard</span>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <section className={styles.header} style={{ backgroundColor: '#12274e' }}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <h2 className={styles.welcomeTitle}>
              Hello {user?.first_name} {user?.last_name}
            </h2>
            <h4 className={styles.welcomeSubtitle}>Guardian Manager Dashboard</h4>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.mainContent}>
        <div className={styles.container}>

          {/* Statistics Overview */}
          <div className={styles.statusOverview}>
            <div className={styles.statusHeader}>
              <h3>Statistics Overview</h3>
            </div>
            <div className={styles.statusBoxes}>
              <Link to="/guardians" className={styles.statusBox}>
                <p className={styles.statusValue}>{statistics.totalGuardians}</p>
                <p className={styles.statusTitle}>Total Guardians</p>
              </Link>

              <Link to="/guardians?status=placed" className={styles.statusBox}>
                <p className={styles.statusValue}>{statistics.placedGuardians}</p>
                <p className={styles.statusTitle}>Placed Guardians</p>
              </Link>

              <Link to="/guardians?status=documents-required" className={styles.statusBox}>
                <p className={styles.statusValue}>{statistics.documentsRequired}</p>
                <p className={styles.statusTitle}>Documents Required</p>
              </Link>

              <Link to="/guardian-queries" className={styles.statusBox}>
                <p className={styles.statusValue}>{statistics.pendingQueries}</p>
                <p className={styles.statusTitle}>Pending Queries</p>
              </Link>

              <Link to="/guardians/after-deadline" className={styles.statusBox}>
                <p className={styles.statusValue}>{statistics.guardiansAfterDeadline}</p>
                <p className={styles.statusTitle}>After Deadline</p>
              </Link>

              <div className={styles.statusBox}>
                <p className={styles.statusValue}>{statistics.guardiansInProcess}</p>
                <p className={styles.statusTitle}>In Process</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className={styles.quickActions}>
            <h3>Quick Actions</h3>
            <div className={styles.actionButtons}>
              <Link to="/guardians/create" className={styles.actionBtn}>
                <i className="fa fa-plus-circle"></i> Add New Guardian
              </Link>
              <Link to="/guardians" className={styles.actionBtn}>
                <i className="fa fa-users"></i> View All Guardians
              </Link>
              <Link to="/guardian-queries/create" className={styles.actionBtn}>
                <i className="fa fa-question-circle"></i> Create Query
              </Link>
              <Link to="/guardians/after-deadline" className={styles.actionBtn}>
                <i className="fa fa-clock-o"></i> After Deadline
              </Link>
            </div>
          </div>

          {/* Guardians After Deadline Table */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>Guardians After Deadline ({processedDeadlineGuardians.total})</h3>
            </div>
            <div className={styles.panelBody}>
              {/* Table Controls */}
              <div className={styles.tableControls}>
                <div className={styles.tableControlsLeft}>
                  <span>Show </span>
                  <select
                    value={deadlinePerPage}
                    onChange={(e) => {
                      setDeadlinePerPage(Number(e.target.value));
                      setDeadlineCurrentPage(1);
                    }}
                    className={styles.selectInput}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span> entries</span>
                </div>
                <div className={styles.tableControlsRight}>
                  <span>Search: </span>
                  <input
                    type="text"
                    value={deadlineSearch}
                    onChange={(e) => {
                      setDeadlineSearch(e.target.value);
                      setDeadlineCurrentPage(1);
                    }}
                    className={styles.searchInput}
                    placeholder="Search guardians..."
                  />
                </div>
              </div>

              {/* Export and Column Visibility Controls */}
              <div className={styles.tableActions}>
                <div className={styles.exportButtons}>
                  <button onClick={handleExportDeadlineCSV} className={styles.exportBtn}>
                    Export CSV
                  </button>
                  <button onClick={handleExportDeadlinePDF} className={styles.exportBtn}>
                    Export PDF
                  </button>
                </div>
                <div className={styles.columnVisibilityContainer}>
                  <button
                    onClick={() => setShowDeadlineColumnVisibility(!showDeadlineColumnVisibility)}
                    className={styles.columnVisibilityBtn}
                  >
                    Column Visibility
                  </button>
                  {showDeadlineColumnVisibility && (
                    <div className={styles.columnVisibilityDropdown}>
                      <h4>Toggle Columns:</h4>
                      {Object.entries({
                        name: 'Name',
                        email: 'Email',
                        property: 'Property',
                        room: 'Room',
                        deadline: 'Deadline',
                        actions: 'Actions'
                      }).map(([key, label]) => (
                        <label key={key} className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={deadlineColumnVisibility[key]}
                            onChange={() => toggleDeadlineColumnVisibility(key)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {processedDeadlineGuardians.data.length > 0 ? (
                <>
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          {deadlineColumnVisibility.name && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('name', deadlineSortConfig, setDeadlineSortConfig)}
                            >
                              Name
                              <SortIcon
                                direction={deadlineSortConfig.key === 'name' ? deadlineSortConfig.direction : null}
                                isActive={deadlineSortConfig.key === 'name'}
                              />
                            </th>
                          )}
                          {deadlineColumnVisibility.email && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('email', deadlineSortConfig, setDeadlineSortConfig)}
                            >
                              Email
                              <SortIcon
                                direction={deadlineSortConfig.key === 'email' ? deadlineSortConfig.direction : null}
                                isActive={deadlineSortConfig.key === 'email'}
                              />
                            </th>
                          )}
                          {deadlineColumnVisibility.property && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('property', deadlineSortConfig, setDeadlineSortConfig)}
                            >
                              Property
                              <SortIcon
                                direction={deadlineSortConfig.key === 'property' ? deadlineSortConfig.direction : null}
                                isActive={deadlineSortConfig.key === 'property'}
                              />
                            </th>
                          )}
                          {deadlineColumnVisibility.room && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('room', deadlineSortConfig, setDeadlineSortConfig)}
                            >
                              Room
                              <SortIcon
                                direction={deadlineSortConfig.key === 'room' ? deadlineSortConfig.direction : null}
                                isActive={deadlineSortConfig.key === 'room'}
                              />
                            </th>
                          )}
                          {deadlineColumnVisibility.deadline && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('deadline', deadlineSortConfig, setDeadlineSortConfig)}
                            >
                              Deadline
                              <SortIcon
                                direction={deadlineSortConfig.key === 'deadline' ? deadlineSortConfig.direction : null}
                                isActive={deadlineSortConfig.key === 'deadline'}
                              />
                            </th>
                          )}
                          {deadlineColumnVisibility.actions && (
                            <th className={styles.actionHead}>Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {processedDeadlineGuardians.data.map((guardian) => (
                          <tr key={guardian.id}>
                            {deadlineColumnVisibility.name && (
                              <td>
                                <Link
                                  to={`/guardians/${guardian.id}`}
                                  className={styles.guardianLink}
                                >
                                  {guardian.user?.first_name} {guardian.user?.last_name}
                                </Link>
                              </td>
                            )}
                            {deadlineColumnVisibility.email && (
                              <td>{guardian.user?.email}</td>
                            )}
                            {deadlineColumnVisibility.property && (
                              <td>
                                <Link
                                  to={`/properties/${guardian.rents?.[0]?.room?.property?.id}`}
                                  className={styles.propertyLink}
                                >
                                  {guardian.rents?.[0]?.room?.property?.name}
                                </Link>
                              </td>
                            )}
                            {deadlineColumnVisibility.room && (
                              <td>{guardian.rents?.[0]?.room?.name}</td>
                            )}
                            {deadlineColumnVisibility.deadline && (
                              <td>
                                {guardian.rents?.[0]?.date_to_send_documentation
                                  ? new Date(guardian.rents[0].date_to_send_documentation).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                            )}
                            {deadlineColumnVisibility.actions && (
                              <td className={styles.actionCell}>
                                <div className={styles.actionButtons}>
                                  <Link
                                    to={`/guardians/${guardian.id}`}
                                    className={styles.btnSecondary}
                                  >
                                    View
                                  </Link>
                                  <Link
                                    to={`/guardians/${guardian.id}/edit`}
                                    className={styles.btnPrimary}
                                  >
                                    Edit
                                  </Link>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {processedDeadlineGuardians.totalPages > 1 && (
                    <div className={styles.pagination}>
                      <span className={styles.paginationInfo}>
                        Showing {((deadlineCurrentPage - 1) * deadlinePerPage) + 1} to {Math.min(deadlineCurrentPage * deadlinePerPage, processedDeadlineGuardians.total)} of {processedDeadlineGuardians.total} entries
                      </span>
                      <div className={styles.paginationControls}>
                        <button
                          onClick={() => setDeadlineCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={deadlineCurrentPage === 1}
                          className={styles.paginationBtn}
                        >
                          Previous
                        </button>
                        {Array.from({ length: processedDeadlineGuardians.totalPages }, (_, i) => i + 1)
                          .filter(page => page === 1 || page === processedDeadlineGuardians.totalPages || Math.abs(page - deadlineCurrentPage) <= 1)
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && <span className={styles.paginationEllipsis}>...</span>}
                              <button
                                onClick={() => setDeadlineCurrentPage(page)}
                                className={`${styles.paginationBtn} ${deadlineCurrentPage === page ? styles.active : ''}`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))}
                        <button
                          onClick={() => setDeadlineCurrentPage(prev => Math.min(processedDeadlineGuardians.totalPages, prev + 1))}
                          disabled={deadlineCurrentPage === processedDeadlineGuardians.totalPages}
                          className={styles.paginationBtn}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.noData}>
                  <p>{deadlineSearch ? 'No guardians found matching search criteria.' : 'No guardians after deadline.'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Guardians in Process Section */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>Guardians in Process ({processedProcessGuardians.total})</h3>
            </div>
            <div className={styles.panelBody}>
              {processedProcessGuardians.data.length > 0 ? (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Guardian Name</th>
                        <th>Property Name</th>
                        <th>Room</th>
                        <th className={styles.actionHead}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedProcessGuardians.data.map((guardian) => (
                        <tr key={guardian.id}>
                          <td>
                            <Link
                              to={`/guardians/${guardian.guardian?.id}`}
                              className={styles.guardianLink}
                            >
                              {guardian.guardian?.user?.first_name} {guardian.guardian?.user?.last_name}
                            </Link>
                          </td>
                          <td>
                            <Link
                              to={`/properties/${guardian.property?.id}`}
                              className={styles.propertyLink}
                            >
                              {guardian.property?.name}
                            </Link>
                          </td>
                          <td>{guardian.room?.name}</td>
                          <td className={styles.actionCell}>
                            <Link
                              to={`/guardian-placement/step2/${guardian.property?.id}/${guardian.guardian?.id}/${guardian.room?.id}`}
                              className={styles.btnPrimary}
                            >
                              Proceed to Step 2
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noData}>
                  <p>No guardians in process.</p>
                </div>
              )}
            </div>
          </div>

          {/* Pending Queries Section */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>Pending Guardian Queries ({processedQueries.total})</h3>
            </div>
            <div className={styles.panelBody}>
              {processedQueries.data.length > 0 ? (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Guardian Name</th>
                        <th>Subject</th>
                        <th>Date</th>
                        <th className={styles.actionHead}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedQueries.data.map((query) => (
                        <tr key={query.id}>
                          <td>
                            <Link
                              to={`/guardians/${query.guardian?.id}`}
                              className={styles.guardianLink}
                            >
                              {query.guardian?.user?.first_name} {query.guardian?.user?.last_name}
                            </Link>
                          </td>
                          <td>{query.subject}</td>
                          <td>{query.created_at ? new Date(query.created_at).toLocaleDateString() : 'N/A'}</td>
                          <td className={styles.actionCell}>
                            <Link
                              to={`/guardian-queries/${query.id}`}
                              className={styles.btnPrimary}
                            >
                              View & Reply
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noData}>
                  <p>No pending queries.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default GuardianManagerDashboard;
